import moment from 'moment';
import { createContext, useContext, useMemo } from 'react';
import useAppointmentActions from '../hooks/APIHooks/appointments/useAppointmentActions';
import useAppointmentRequestActions from '../hooks/APIHooks/appointments/useAppointmentRequestActions';
import { AppointmentRequest } from '../types/appointmentRequests';
import { User } from '../types/users';
import { showTimeIn12HrsFormat } from '../utils/timeAndDate';
import { useAuthContext } from './AuthProvider';

// datatype for the context declared here
interface IAppointmentHistoryContext {
  isLoading?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
  appointmentHistoryList: Array<AppointmentRequest>;
}

// creating the context here
const AppointmentHistoryContext =
  createContext<IAppointmentHistoryContext | null>(null);

const AppointmentHistoryProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // integration of context hooks here
  const { currentUser, currentUserType } = useAuthContext();

  // integration of custom API hooks here
  const { getAppointmentReqList } = useAppointmentRequestActions(
    (currentUser as User)?.role_name === 'Admin' ? 'appointmentReqList' : ''
  );
  const {
    getAppointmentsByAssociateIdHandler,
    getAppointmentsByClientIdHandler,
  } = useAppointmentActions(
    '',
    currentUserType === 'user' && (currentUser as User)?.role_name !== 'Admin'
      ? currentUser?.id
      : undefined,
    currentUserType === 'client' ? currentUser?.id : undefined,
    'all'
  );

  const appointmentHistoryList: Array<AppointmentRequest> = useMemo(() => {
    if (!currentUser) return [];

    if ((currentUser as User).role_name === 'Admin') {
      if (!getAppointmentReqList.data?.is_success) return [];

      return getAppointmentReqList.data.data.map((appointment) => ({
        ...appointment,
        date: moment(appointment.date).format('DD-MM-YYYY'),
        start_time: showTimeIn12HrsFormat(appointment.start_time),
        end_time: showTimeIn12HrsFormat(appointment.end_time),
        status:
          appointment.reschedule_attempts > 0
            ? 'Rescheduled'
            : appointment.is_complete
            ? 'Completed'
            : new Date(`${appointment.date} ${appointment.start_time}`) >
              new Date()
            ? 'Upcoming'
            : 'Unattended',
      }));
    }

    if (
      currentUserType === 'user' &&
      (currentUser as User).role_name !== 'Admin'
    ) {
      if (!getAppointmentsByAssociateIdHandler.data?.is_success) return [];

      return getAppointmentsByAssociateIdHandler.data.data.map(
        (appointment) => ({
          ...appointment,
          date: moment(appointment.date).format('DD-MM-YYYY'),
          start_time: showTimeIn12HrsFormat(appointment.start_time),
          end_time: showTimeIn12HrsFormat(appointment.end_time),
          status:
            appointment.reschedule_attempts > 0
              ? 'Rescheduled'
              : appointment.is_complete
              ? 'Completed'
              : new Date(`${appointment.date} ${appointment.start_time}`) >
                new Date()
              ? 'Upcoming'
              : 'Unattended',
        })
      );
    }

    if (!getAppointmentsByClientIdHandler.data?.is_success) return [];

    return getAppointmentsByClientIdHandler.data.data.map((appointment) => ({
      ...appointment,
      date: moment(appointment.date).format('DD-MM-YYYY'),
      start_time: showTimeIn12HrsFormat(appointment.start_time),
      end_time: showTimeIn12HrsFormat(appointment.end_time),
      status:
        appointment.reschedule_attempts > 0
          ? 'Rescheduled'
          : appointment.is_complete
          ? 'Completed'
          : new Date(`${appointment.date} ${appointment.start_time}`) >
            new Date()
          ? 'Upcoming'
          : 'Unattended',
    }));
  }, [
    currentUser,
    currentUserType,
    getAppointmentReqList.data,
    getAppointmentsByAssociateIdHandler.data,
    getAppointmentsByClientIdHandler.data,
  ]);

  // setting the appointment history list here
  // useEffect(() => {
  //   setAppointmentHistoryList([]);

  //   if ((currentUser as User)?.role_name === 'Admin') {
  //     if (getAppointmentReqList.data?.is_success) {
  //       setAppointmentHistoryList(
  //         getAppointmentReqList.data.data.map((appointment) => ({
  //           ...appointment,
  //           date: moment(appointment.date).format('DD-MM-YYYY'),
  //           start_time: showTimeIn12HrsFormat(appointment.start_time),
  //           end_time: showTimeIn12HrsFormat(appointment.end_time),
  //           status:
  //             appointment.reschedule_attempts > 0
  //               ? 'Rescheduled'
  //               : appointment.is_complete
  //               ? 'Completed'
  //               : new Date(`${appointment.date} ${appointment.start_time}`) >
  //                 new Date()
  //               ? 'Upcoming'
  //               : 'Unattended',
  //         }))
  //       );
  //     }
  //   } else if (
  //     currentUserType === 'user' &&
  //     (currentUser as User)?.role_name !== 'Admin'
  //   ) {
  //     if (getAppointmentsByAssociateIdHandler.data?.is_success) {
  //       setAppointmentHistoryList(
  //         getAppointmentsByAssociateIdHandler.data.data.map((appointment) => ({
  //           ...appointment,
  //           date: moment(appointment.date).format('DD-MM-YYYY'),
  //           start_time: showTimeIn12HrsFormat(appointment.start_time),
  //           end_time: showTimeIn12HrsFormat(appointment.end_time),
  //           status:
  //             appointment.reschedule_attempts > 0
  //               ? 'Rescheduled'
  //               : appointment.is_complete
  //               ? 'Completed'
  //               : new Date(`${appointment.date} ${appointment.start_time}`) >
  //                 new Date()
  //               ? 'Upcoming'
  //               : 'Unattended',
  //         }))
  //       );
  //     }
  //   } else if (currentUserType === 'client') {
  //     if (getAppointmentsByClientIdHandler.data?.is_success) {
  //       setAppointmentHistoryList(
  //         getAppointmentsByClientIdHandler.data.data.map((appointment) => ({
  //           ...appointment,
  //           date: moment(appointment.date).format('DD-MM-YYYY'),
  //           start_time: showTimeIn12HrsFormat(appointment.start_time),
  //           end_time: showTimeIn12HrsFormat(appointment.end_time),
  //           status:
  //             appointment.reschedule_attempts > 0
  //               ? 'Rescheduled'
  //               : appointment.is_complete
  //               ? 'Completed'
  //               : new Date(`${appointment.date} ${appointment.start_time}`) >
  //                 new Date()
  //               ? 'Upcoming'
  //               : 'Unattended',
  //         }))
  //       );
  //     }
  //   }
  // }, [
  //   currentUser,
  //   currentUserType,
  //   getAppointmentReqList.data,
  //   getAppointmentsByAssociateIdHandler.data,
  //   getAppointmentsByClientIdHandler.data,
  // ]);

  // rendering the context provider here
  return (
    <AppointmentHistoryContext.Provider
      value={{
        isLoading:
          (currentUser as User)?.role_name === 'Admin'
            ? getAppointmentReqList.isLoading
            : currentUserType === 'user' &&
              (currentUser as User)?.role_name !== 'Admin'
            ? getAppointmentsByAssociateIdHandler.isLoading
            : getAppointmentsByClientIdHandler.isLoading,
        isError:
          (currentUser as User)?.role_name === 'Admin'
            ? getAppointmentReqList.isError
            : currentUserType === 'user' &&
              (currentUser as User)?.role_name !== 'Admin'
            ? getAppointmentsByAssociateIdHandler.isError
            : getAppointmentsByClientIdHandler.isError,
        isSuccess:
          (currentUser as User)?.role_name === 'Admin'
            ? getAppointmentReqList.isSuccess
            : currentUserType === 'user' &&
              (currentUser as User)?.role_name !== 'Admin'
            ? getAppointmentsByAssociateIdHandler.isSuccess
            : getAppointmentsByClientIdHandler.isSuccess,
        appointmentHistoryList,
      }}
    >
      {children}
    </AppointmentHistoryContext.Provider>
  );
};

export default AppointmentHistoryProvider;

// custom hook to access appointment history context declared here
export const useAppointmentHistoryContext = () => {
  const context = useContext(AppointmentHistoryContext);

  if (!context) {
    throw new Error(
      'useAppointmentHistoryContext must be within AppointmentHistoryProvider!'
    );
  }

  return context;
};
